import type { ConvertableUnit } from "@/libs/units";
import {
  PSI_ATM_CONVERSION_FACTOR,
  PSI_BAR_CONVERSION_FACTOR
} from "@/libs/calculations/constants";
import { buildOptions } from "./buildOptions";

// STANDARD UNIT
export const PoundsPerSquareInchAbsolute: ConvertableUnit = {
  label: "PSIA",
  toStandardUnit: psia => psia,
  toThisUnit: psia => psia
};

export const PoundsPerSquareInchGauge: ConvertableUnit = {
  label: "PSIG",
  toStandardUnit: psig => psig + 14.6959487755,
  toThisUnit: psia => psia - 14.6959487755
};

/** PSI differential – for pressure deltas; same scale as PSIA, no conversion. */
export const PoundsPerSquareInchDifferential: ConvertableUnit = {
  label: "PSID",
  toStandardUnit: x => x,
  toThisUnit: x => x
};

export const Pascal: ConvertableUnit = {
  label: "Pa",
  toStandardUnit: pa => pa / (PSI_BAR_CONVERSION_FACTOR * 100000),
  toThisUnit: psia => psia * (PSI_BAR_CONVERSION_FACTOR * 100000)
};

export const KiloPascals: ConvertableUnit = {
  label: "kPa",
  toStandardUnit: kp => kp / (PSI_BAR_CONVERSION_FACTOR * 100),
  toThisUnit: psia => psia * (PSI_BAR_CONVERSION_FACTOR * 100)
};

export const KilogramsPerSquareCentiMeter: ConvertableUnit = {
  label: "kgf/cm2",
  toStandardUnit: kgfcm2 => kgfcm2 / 0.070306958,
  toThisUnit: psia => psia * 0.070306958
};

export const Bar: ConvertableUnit = {
  label: "Bar",
  toStandardUnit: bar => bar / PSI_BAR_CONVERSION_FACTOR,
  toThisUnit: psia => psia * PSI_BAR_CONVERSION_FACTOR
};

export const Atm: ConvertableUnit = {
  label: "Atm",
  toStandardUnit: atm => atm / PSI_ATM_CONVERSION_FACTOR,
  toThisUnit: psia => psia * PSI_ATM_CONVERSION_FACTOR
};

/** Use for differential pressure display: PSIG/PSIA → PSID so the delta is never offset. */
export function effectiveDifferentialPressureUnit(
  unit: ConvertableUnit
): ConvertableUnit {
  return unit === PoundsPerSquareInchGauge ||
    unit === PoundsPerSquareInchAbsolute
    ? PoundsPerSquareInchDifferential
    : unit;
}

const unitList = [
  PoundsPerSquareInchAbsolute,
  PoundsPerSquareInchGauge,
  PoundsPerSquareInchDifferential,
  KiloPascals,
  KilogramsPerSquareCentiMeter,
  Bar,
  Atm
];

/** Options for differential pressure only: no PSIG/PSIA so the delta is never offset. */
const differentialUnitList = unitList.filter(
  u => u !== PoundsPerSquareInchGauge && u !== PoundsPerSquareInchAbsolute
);

export const PressureUnits = {
  PoundsPerSquareInchAbsolute,
  PoundsPerSquareInchGauge,
  PoundsPerSquareInchDifferential,
  Pascal,
  KiloPascals,
  KilogramsPerSquareCentiMeter,
  Bar,
  Atm,
  options: buildOptions(unitList),
  /** Same shape as options(): call with () to get ConvertableUnit[] for differential (no PSIG/PSIA). */
  optionsForDifferential: buildOptions(differentialUnitList)
};
